<template>
    <VTaskPanel
        :board="board"
        :can-create-sub-tasks="canCreateSubTasks"
        :parent="parent"
        :project="project"
        :subtasks="subtasks"
        :task="task"
        :uploader="uploader"
        @close="close"
        v-if="task"
    ></VTaskPanel>
</template>

<script setup lang="ts">
// Components
import VTaskPanel from '../components/VTaskPanel.vue';

// Other
import { pushPageTitle, pullPageTitle } from '@/utils/document-utils';
import ITask from '@/core/Models/ITask';
import TaskService from '@/core/Services/TaskService';
import UserMapper from '@/core/UserMapper';
import emitter from '@/core/Emitter';
import { EventNames } from '@/core/EventNames';
import TaskType from '@/core/Values/TaskType';
import { Raw, markRaw, ref, onMounted, watch, defineEmits } from 'vue';
import { TaskMutatorContext } from '@/core/Mutators/TaskMutator';
import MutationBus from '@/core/Mutations/MutationBus';
import IMutatorContext from '@/core/Mutations/IMutatorContext';
import ObjectStorageMapper from '@/core/ObjectStorageMapper';
import { objectStorageUploader } from '@/core/Uploader/UploaderBuilder';
import { $error } from '@/utils/app-utils';

import store from '@/store';
import IBoard from '@/core/Models/IBoard';
import IProject from '@/core/Models/IProject';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { computed } from '@vue/reactivity';
import { onBeforeUnmount } from 'vue';
import scrollIntoView from 'scroll-into-view';

const emit = defineEmits(['configure', 'loading', 'no-data', 'close', 'before-unmount']);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

let task = ref(null as ITask | null);
let parent = ref(null as ITask | null);
let subtasks = ref(null as ITask[] | null);
let uploader = objectStorageUploader;
let mutatorContexts: Raw<IMutatorContext[]> | null = null;

const taskId = computed(() => parseInt(route.query?.task as string) || 0);

const board = computed((): IBoard | undefined => {
    return store.state.boards?.find((board) => board.id === task.value?.boardId);
});

const project = computed((): IProject | undefined => {
    return store.state.projects?.find((project) => project.id === task.value?.projectId);
});

const canCreateSubTasks = computed((): boolean => {
    return task.value?.type === TaskType.Task;
});

emitter.on(EventNames.ConnectionLoopReconnected, fetchTask);

watch(() => task.value?.parentId, onParentChanged, { deep: true });
watch(
    () => route.query?.task,
    (newTaskId, oldTaskId) => {
        scrollIntoTask(taskId.value);
        pullPageTitle(t('task-number', { number: oldTaskId }));
        pushPageTitle(t('task-number', { number: newTaskId }));
        fetchTask();
    },
);

onMounted(() => {
    scrollIntoTask(taskId.value);
    emit('configure', { close });
    pushPageTitle(t('task-number', { number: taskId.value }));
    fetchTask();
});

onBeforeUnmount((): void => {
    emit('before-unmount');
    pullPageTitle(t('task-number', { number: task.value?.id }));
    // Deactivate old context, to avoid memory leaks.
    MutationBus.deactivate(mutatorContexts);
    emitter.off(EventNames.ConnectionLoopReconnected, fetchTask);
});

function close(force = false): void {
    router.replace({
        query: {
            ...route.query,
            prevTask: undefined,
            prevSelected: undefined,
            task: force ? undefined : route.query.prevTask,
            selected: force ? undefined : route.query.prevSelected,
        },
    });
}

function scrollIntoTask(taskId: number | string) {
    if (!taskId) {
        return;
    }

    const taskElement = document.querySelector(`.task-selector[task-id='${taskId}']`) as HTMLElement;
    if (taskElement) {
        scrollIntoView(taskElement, {
            time: 250,
        });
    }
}

async function fetchTask(): Promise<void> {
    emit('loading', true);

    try {
        const result = await TaskService.queryAsync({
            whereId: [taskId.value],
            whereType: [TaskType.Task, TaskType.Subtask],
            withArchived: true,
            perPage: 1,
            includes: ['stories', 'approvements', 'collaborators', 'save-points-count'],
        });

        const item = result.length > 0 ? result[0] : null;

        if (!item) {
            task.value = null;
            emit('no-data', true);
            return;
        }

        if (item.parentId) {
            await fetchParent(item.parentId);
        }

        task.value = item;
        subtasks.value = null;

        UserMapper.mapTaskAsync(task.value);
        ObjectStorageMapper.mapTaskAsync(task.value);

        // Creates the mutation context for watching for task.
        const contexts: IMutatorContext[] = [
            new TaskMutatorContext([task.value], {
                mapUsers: true,
                mapAttachments: true,
                ignoreTaskCreating: () => true,
            }),
        ];

        // Loads the subtasks if the task can contains the subtasks.
        if (task.value.type === TaskType.Task) {
            subtasks.value = await TaskService.queryAsync({
                whereTaskId: [taskId.value],
                whereType: [TaskType.Subtask],
                includes: ['comments-count', 'attachments-count', 'collaborators', 'approvements'],
            });

            UserMapper.mapTasksAsync(subtasks.value);

            contexts.push(
                new TaskMutatorContext(subtasks.value, {
                    mapUsers: true,
                    ignoreTaskCreating: (item) => item.parentId !== task.value?.id,
                    excludeTask: (item) => !!item.archivedAt,
                }),
            );
        }

        // Deactivate old context, to avoid memory leaks.
        MutationBus.deactivate(mutatorContexts);
        mutatorContexts = markRaw(contexts);
        MutationBus.activate(mutatorContexts);
    } catch (error) {
        $error(error);
    } finally {
        emit('loading', false);
    }
}

async function fetchParent(parentId: number): Promise<void> {
    const tasks = await TaskService.queryAsync({
        whereId: [parentId],
        perPage: 1,
        withArchived: true,
    });

    parent.value = tasks.length > 0 ? tasks[0] : null;
}

function onParentChanged(newValue: number | undefined | null, oldValue: number | undefined | null): void {
    if (!task.value?.parentId) {
        parent.value = null;
    }

    if (newValue !== oldValue && task.value?.parentId && parent.value?.id !== newValue) {
        fetchParent(task.value?.parentId);
    }
}
</script>
